/**
 * The addresses Integrations → Microsoft 365 reads and writes for AI agent
 * access (plan_25b45738, plan_4cbf7091): Microsoft's step-2 consent return,
 * the bring-your-own return link, command one, and the redirect of every old
 * settings address to the page.
 */

/** The Microsoft 365 page (features/msgraph/routes.jsx). */
const PAGE = '/microsoft-365'

/**
 * The Microsoft 365 page with an old address's parameters (consent and
 * bring-your-own returns included); only the settings `tab` is dropped.
 */
export function microsoft365Address(search) {
  const params = new URLSearchParams(search)
  params.delete('tab')
  const query = params.toString()
  return query ? `${PAGE}?${query}` : PAGE
}

const AREA_KEY = /^[a-z0-9-]{1,32}$/

/** Microsoft's error_description, first line, without its trace and correlation ids. */
export function microsoftDescription(raw) {
  return String(raw || '').split(/\r?\n/)[0]
    .replace(/\s*(?:Trace ID|Correlation ID):\s*[0-9a-f-]{8,}/gi, '')
    .replace(/\s*Timestamp:\s*\d{4}-\d{2}-\d{2}[ T][0-9:.]+Z?/gi, '')
    .trim()
}

/**
 * Step 2 of "Approve area" (the console app's admin consent for the area's
 * blueprint) returns to the console origin with Microsoft's answer in the
 * query and state `<area>:console`. Before MSAL reads the address, this moves
 * that answer to the Microsoft 365 page:
 * /microsoft-365?area=…&agent_console_consent=success|error[&detail=…&description=…].
 * Returns whether it did.
 */
export function captureAgentConsoleConsentReturn(win) {
  const params = new URLSearchParams(win.location.search)
  const state = params.get('state') || ''
  const match = /^([a-z0-9-]{1,32}):console$/.exec(state)
  if (!match || !(params.has('admin_consent') || params.has('error'))) return false
  const next = new URLSearchParams({ area: match[1] })
  const error = params.get('error')
  if (error || (params.get('admin_consent') || '').toLowerCase() !== 'true') {
    next.set('agent_console_consent', 'error')
    next.set('detail', error || 'consent_not_granted')
    const description = microsoftDescription(params.get('error_description'))
    if (description) next.set('description', description)
  } else {
    next.set('agent_console_consent', 'success')
  }
  win.history.replaceState(null, '', `${PAGE}?${next}`)
  return true
}

/** The consent-return facts on the Microsoft 365 page's address, or null. */
export function readAreaConsentReturn(search) {
  const params = new URLSearchParams(search)
  for (const [param, step] of [['agent_area_consent', 'blueprint'], ['agent_console_consent', 'console']]) {
    const status = params.get(param)
    if (status !== 'success' && status !== 'error') continue
    const area = params.get('area') || ''
    if (!AREA_KEY.test(area)) continue
    return { step, status, area, detail: params.get('detail') || '', description: params.get('description') || '' }
  }
  return null
}

/** The compact return link carries `v` or `blueprints`; the long one carries neither. */
export const isCompactLink = (link) => Boolean(link) && typeof link === 'object' && ('v' in link || 'blueprints' in link)

/**
 * The setup's return link payload (`?byob=<urlencoded JSON>`). Two links
 * come back here:
 *   - step two's blueprint link, as the broker README pins it:
 *     { brokerUrl, tenantId, areas } → { link } (Bring your own blueprints),
 *     or its compact form { v: 2, brokerUrl, tenantId, blueprints } (the
 *     setup prints it when setup.json advertises returnLinks: ['compact']),
 *     returned as it came: expandByobLink reads it with the console's areas;
 *   - the mail step's link, as operations-platform docs/agent-broker-mail.md
 *     pins it: { brokerUrl, tenantId, mailbox, mailIdentity: { objectId } }
 *     and no `areas` → { mailLink } (the Email section).
 * The worker validates every field again; this only refuses what is not even
 * one of those shapes.
 */
export function readByobLink(search) {
  const raw = new URLSearchParams(search).get('byob')
  if (raw === null) return null
  let link
  try {
    link = JSON.parse(raw)
  } catch (error) {
    return { error: `The link's byob value is not JSON: ${error.message}` }
  }
  if (link && typeof link === 'object' && !('areas' in link) && 'mailIdentity' in link) {
    if (typeof link.brokerUrl !== 'string' || typeof link.tenantId !== 'string' || typeof link.mailbox !== 'string'
      || !link.mailIdentity || typeof link.mailIdentity.objectId !== 'string') {
      return { mailError: 'The mail link carries no { brokerUrl, tenantId, mailbox, mailIdentity: { objectId } }.' }
    }
    return { mailLink: link }
  }
  if (isCompactLink(link)) {
    if (typeof link.brokerUrl !== 'string' || typeof link.tenantId !== 'string' || !link.blueprints || typeof link.blueprints !== 'object') {
      return { error: 'The link carries no { v, brokerUrl, tenantId, blueprints }.' }
    }
    return { link }
  }
  if (!link || typeof link !== 'object' || typeof link.brokerUrl !== 'string' || typeof link.tenantId !== 'string' || !Array.isArray(link.areas)) {
    return { error: 'The link carries no { brokerUrl, tenantId, areas }.' }
  }
  return { link }
}

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The return link as the long one, { link } or { error }. A compact link
 * names each area by key with its blueprint; its label and Microsoft Graph
 * permissions come from `areas`, the setup areas GET
 * /api/msgraph/bring-your-own serves (the same list setup.json gives the
 * script), in their order. A long link is returned as it came. The worker
 * expands the compact link again from the same catalogue and validates every
 * field; this refuses what the panel could not even show.
 */
export function expandByobLink(link, areas) {
  if (!isCompactLink(link)) return { link }
  if (link.v !== 2) return { error: `The link is a compact link of version ${JSON.stringify(link.v ?? null)}; this console reads version 2.` }
  const entries = link.blueprints && typeof link.blueprints === 'object' && !Array.isArray(link.blueprints) ? Object.entries(link.blueprints) : []
  if (!entries.length) return { error: 'The link carries no blueprints ({ <area key>: <blueprint app id> }).' }
  const known = new Map((areas || []).map((area) => [area.key, area]))
  const ids = new Map()
  for (const [key, id] of entries) {
    if (!known.has(key)) return { error: `The link names area "${key}", which this console does not know.` }
    if (typeof id !== 'string' || !GUID.test(id)) return { error: `The link's blueprint for ${key} is not a UUID: ${JSON.stringify(id ?? null)}` }
    ids.set(key, id.toLowerCase())
  }
  return {
    link: {
      brokerUrl: link.brokerUrl,
      tenantId: link.tenantId,
      areas: areas.filter((area) => ids.has(area.key)).map((area) => ({ key: area.key, label: area.label, blueprintAppId: ids.get(area.key), graphScopes: area.graphScopes })),
    },
  }
}

/**
 * Microsoft's answer to the consent that adds the mail app, as the admin's
 * callback forwards it: /microsoft-365?mail_consent=success|error[&detail=…&description=…].
 */
export function readMailConsentReturn(search) {
  const params = new URLSearchParams(search)
  const status = params.get('mail_consent')
  if (status !== 'success' && status !== 'error') return null
  return { status, detail: params.get('detail') || '', description: microsoftDescription(params.get('description')) }
}

/** Where this console serves one published commit's setup files: its own public API origin. */
const CONSOLE_SOURCE = /^https:\/\/[A-Za-z0-9.-]+(:[0-9]{1,5})?\/api\/public\/agent-broker\/[a-f0-9]{40}$/

/**
 * Command one, exactly as agent-broker/README.md in operations-platform
 * defines it: the published setup script at `source` and this console's
 * origin. `source` is the console's own public API origin serving the pinned
 * commit (GET /api/msgraph/bring-your-own), so the script and every file it
 * downloads come from the customer's own domain; the script reads this
 * console's sign-in app and the catalogue areas from `<source>/setup.json`.
 */
export function buildCommandOne({ source, origin }) {
  if (!CONSOLE_SOURCE.test(String(source))) {
    throw new Error(`The console did not name its own setup-file address (https://<console API>/api/public/agent-broker/<commit>): ${JSON.stringify(source ?? null)}`)
  }
  return `curl -fsSL ${source}/setup.sh | bash -s -- azure --source ${source} --console ${origin}`
}
